import React, { useEffect, useState, useCallback } from 'react';
import {
  getDashboard,
  getUnitProgression,
  getAllGateReadiness,
  getCurriculumConfig,
  DashboardData,
  ProgressionData,
  AllUnitsReadinessResponse,
  CurriculumConfig,
} from '../services/api';
import { storage } from '../services/storage';
import { useAuth } from '../contexts/AuthContext';
import { DueReviewsBadge } from '../components/dashboard/DueReviewsBadge';
import { PrimaryActionCard } from '../components/dashboard/PrimaryActionCard';
import { ThisWeekCard } from '../components/dashboard/ThisWeekCard';
import { UnitProgressBar } from '../components/dashboard/UnitProgressBar';
import { LOProgressCard } from '../components/dashboard/LOProgressCard';
import { MisconceptionReport } from '../components/dashboard/MisconceptionReport';
import { MemoryStrengthWidget } from '../components/dashboard/MemoryStrengthWidget';
import { DailyGoalWidget } from '../components/dashboard/DailyGoalWidget';
import { AssignmentCard } from '../components/dashboard/AssignmentCard';
import { QuickPracticeCard } from '../components/dashboard/QuickPracticeCard';
import { CurriculumSelectorModal } from '../components/auth/CurriculumSelectorModal';

const CURRICULUM_LABELS: Record<string, string> = {
  grade6: 'Grade 6 Math',
  grade7: 'Grade 7 Math',
  grade8: 'Grade 8 Math',
  algebra1: 'Algebra 1',
  math1: 'Mathematics I',
};

export const DashboardPage: React.FC = () => {
  const { user, availableCurricula, currentCurriculum, switchCurriculum } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [progression, setProgression] = useState<ProgressionData | null>(null);
  const [gateReadiness, setGateReadiness] = useState<AllUnitsReadinessResponse | null>(null);
  const [curriculumConfig, setCurriculumConfig] = useState<CurriculumConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCurriculumSelector, setShowCurriculumSelector] = useState(false);

  const studentId = storage.getStudentId();

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashboard, prog, readiness, curriculum] = await Promise.all([
        getDashboard(studentId),
        getUnitProgression(studentId).catch(() => null),
        getAllGateReadiness(studentId).catch(() => null),
        getCurriculumConfig(studentId).catch(() => null),
      ]);
      setData(dashboard);
      setProgression(prog);
      setGateReadiness(readiness);
      setCurriculumConfig(curriculum);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleCurriculumSwitch = async (curriculum: string) => {
    await switchCurriculum(curriculum);
    // Dashboard will reload due to studentId change
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  if (error) {
    return (
      <div className="error-page">
        <h2>Could not load dashboard</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // Helper to get gate readiness for a unit
  const getReadinessForUnit = (unitNum: number) => {
    if (!gateReadiness) return undefined;
    const unitReadiness = gateReadiness.units.find(u => u.unit === unitNum);
    if (!unitReadiness) return undefined;
    return {
      readiness_score: unitReadiness.readiness_score,
      readiness_label: unitReadiness.readiness_label,
      message: unitReadiness.message,
      weak_area: unitReadiness.weak_area,
    };
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <div className="dashboard-header-text">
          <h1>{storage.hasSeenTip('welcome') ? 'Welcome back' : 'Welcome'}, {user?.name?.split(' ')[0] || 'Student'}!</h1>
          {curriculumConfig && (
            <button
              className="dashboard-curriculum dashboard-curriculum-switcher"
              onClick={() => setShowCurriculumSelector(true)}
            >
              {CURRICULUM_LABELS[curriculumConfig.curriculum_id] || curriculumConfig.curriculum_id}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
        <DueReviewsBadge count={data.due_reviews} />
      </div>

      {/* Daily Goal Widget */}
      <DailyGoalWidget studentId={studentId} />

      {/* Quick Practice — 5 adaptive questions */}
      <QuickPracticeCard />

      {/* Assignments from Parent */}
      <AssignmentCard studentId={studentId} />

      {/* Primary Action - Hero CTA */}
      <PrimaryActionCard studentId={studentId} totalAttempted={data.overall_attempted} />

      <div className="dashboard-grid streamlined">
        {/* Row 1: This Week + Your Progress */}
        <div className="dashboard-card this-week">
          <ThisWeekCard
            studentId={studentId}
            recentActivity={data.recent_activity}
            streakDays={data.streak_days}
          />
        </div>

        <div className="dashboard-card units-card">
          <h3>Your Progress</h3>
          <div className="units-list">
            {data.units.map(unit => {
              const unitProg = progression?.units.find(u => u.unit === unit.unit);
              return (
                <UnitProgressBar
                  key={unit.unit}
                  unit={unit.unit}
                  name={unit.name}
                  avgMastery={unit.avg_mastery}
                  lessonsTouched={unit.lessons_touched}
                  totalLessons={unit.total_lessons}
                  nextLesson={unit.next_lesson}
                  unlocked={unitProg?.unlocked ?? true}
                  passedGate={unitProg?.passed_gate ?? false}
                  gateReadiness={getReadinessForUnit(unit.unit)}
                />
              );
            })}
          </div>
        </div>

        {/* Row 2: Learning Objectives + Memory Strength */}
        <div className="dashboard-card lo-card">
          <LOProgressCard studentId={studentId} />
        </div>

        <div className="dashboard-card memory-card">
          <MemoryStrengthWidget studentId={studentId} />
        </div>

        {/* Row 3: Recent Mistakes (full width) */}
        <div className="dashboard-card mistakes-card full-width">
          <MisconceptionReport studentId={studentId} />
        </div>
      </div>

      {showCurriculumSelector && (
        <CurriculumSelectorModal
          curricula={availableCurricula}
          currentCurriculum={currentCurriculum}
          onSelect={handleCurriculumSwitch}
          onClose={() => setShowCurriculumSelector(false)}
          showAddGrade={true}
        />
      )}
    </div>
  );
};
